import { useEffect, useState, useCallback } from "react";
import { useOperator } from "@/context/OperatorContext";
import api from "@/lib/api";
import RentalWizard from "@/components/RentalWizard";
import ReturnWizard from "@/components/ReturnWizard";
import { Loader2, Bike } from "lucide-react";

const STAGE_LABEL = { kyc: "KYC", agreement: "Agreement", handover: "Handover", active: "Active", return: "Return", closed: "Closed", booking: "Booking" };
const STAGE_STYLE = {
  active: "bg-status-available/15 text-status-available",
  closed: "bg-status-retired/15 text-status-retired",
  return: "bg-status-overdue/15 text-status-overdue",
  handover: "bg-status-overdue/15 text-status-overdue",
  agreement: "bg-status-rented/15 text-status-rented",
  kyc: "bg-status-rented/15 text-status-rented",
};
const RISK_STYLE = {
  low: "bg-status-available/15 text-status-available",
  medium: "bg-status-overdue/15 text-status-overdue",
  high: "bg-status-recovery/15 text-status-recovery",
};

export default function RentalsPipeline() {
  const { ctx, reload } = useOperator();
  const [bookings, setBookings] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/operator/bookings");
    setBookings(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDone = async () => { await load(); await reload(); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Rentals</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Stage-gated rider pipeline. A rental cannot be handed over with any enabled gate missing.
          </p>
        </div>
        {ctx && <RentalWizard vehicles={ctx.vehicles} onDone={onDone} />}
      </div>

      {!bookings ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : bookings.length === 0 ? (
        <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center">
          <Bike className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No rentals yet. Start a guided rider rental.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-tenant-card border border-tenant-border bg-tenant-surface shadow-tenant-soft">
          <table className="w-full text-sm">
            <thead className="border-b border-tenant-border bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Rider</th>
                <th className="px-4 py-2.5 font-medium">Vehicle</th>
                <th className="px-4 py-2.5 font-medium">Stage</th>
                <th className="px-4 py-2.5 font-medium">Risk</th>
                <th className="px-4 py-2.5 font-medium">Gates</th>
                <th className="px-4 py-2.5 font-medium">Deposit</th>
                <th className="px-4 py-2.5 font-medium">Exposure</th>
                <th className="px-4 py-2.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((bk) => (
                <tr key={bk.id} data-testid={`booking-row-${bk.id}`} className="border-b border-tenant-border last:border-0">
                  <td className="px-4 py-3 font-medium">{bk.customer_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{bk.vehicle_reg} · {bk.vehicle_model}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_STYLE[bk.stage] || "bg-muted text-muted-foreground"}`}>
                      {STAGE_LABEL[bk.stage] || bk.stage}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {bk.risk_band ? (
                      <span data-testid={`risk-${bk.id}`} className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${RISK_STYLE[bk.risk_band]}`}>
                        {bk.risk_band}{bk.risk_total != null ? ` · ${bk.risk_total}` : ""}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={bk.can_handover ? "text-status-available" : "text-muted-foreground"}>
                      {bk.gates_satisfied}/{bk.gates_total}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">₹{(bk.deposit_held || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-muted-foreground">{((bk.exposure_ratio || 0) * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right">
                    {bk.stage === "active" && <ReturnWizard booking={bk} onDone={onDone} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
